import { Outlet, Link, useLocation, useNavigate } from 'react-router';
import { 
  LayoutDashboard, 
  Package, 
  Truck, 
  Users, 
  MapPin, 
  Fuel,
  FileText,
  Menu,
  X,
  Calendar,
  FolderOpen,
  DollarSign,
  Settings as SettingsIcon,
  Bell,
  MessageCircle,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Share2,
  Warehouse,
  Clock,
  FileSignature,
  UserCheck,
  UserPlus,
  AlertTriangle,
  ClipboardList,
  FileCheck,
  CalendarClock,
  LogOut,
  Home,
  CalendarCheck,
  Receipt,
  CheckSquare,
  Banknote,
  Navigation,
  Activity,
  GitBranch,
  TrendingDown,
  ShoppingCart,
  Briefcase,
  UserCircle,
  Shield,
  UserCog,
  FileBarChart,
  Key,
  BookOpen,
  LifeBuoy,
  PanelsTopLeft,
  HardDrive,
  BarChart3,
  TrendingUpIcon,
  Brain,
  LayoutGrid,
  PieChart as PieChartIcon,
  Timer,
  Target,
  FileSpreadsheet,
  Lightbulb,
  CornerUpLeft,
  Building2
} from 'lucide-react';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { initializeSampleData, getUser } from '../utils/storage';
import { useAuth, UserRole } from '../contexts/AuthContext';
import { ROLE_INFO } from '../config/permissions';
import { usePermissions } from '../hooks/usePermissions';
import { notificationService } from '../services/notificationService';
import { supportService } from '../services/supportService';
import { settingsService } from '../services/settingsService';
import { seyirViolationService } from '../services/seyirViolationService';
import { SESSION_EXPIRED_EVENT, SESSION_EXPIRED_MESSAGE_KEY } from '../services/apiService';
import { toast } from 'sonner';
import { announceNewNotifications, primeNotificationAlertCache } from '../utils/inAppNotificationAlert';
import { getStoredThemePreference, getUserThemePreferenceSettingKey, setStoredThemePreference, type ThemePreference } from '../utils/themePreference';
import { isAnyDriverUser } from '../utils/userRoles';
import dogusLogo from '@/assets/599ad72d49c8887573a4750887df95ce60e36150.png';

interface NavItem {
  path: string;
  label: string;
  icon: any;
  children?: NavItem[];
}

const navItems: NavItem[] = [
  { path: '/', label: 'Anasayfa', icon: LayoutDashboard },
  { path: '/live-tracking', label: 'Canlı Takip', icon: Activity },
  { 
    path: '/tracking-list', 
    label: 'Operasyon Yönetimi', 
    icon: Package,
    children: [
      { path: 'tracking-list', label: 'Genel Liste', icon: FileText },
      { path: 'shipment-timeline', label: 'Sevkiyat Timeline', icon: Activity },
      { path: 'fleet-status-list', label: 'Filo Durum Listesi', icon: Truck },
      { path: 'spot-status-list', label: 'Spot Durum Listesi', icon: Truck },
      { path: 'accounting-workflow', label: 'Finansal İşlemler', icon: FileCheck },
      { path: 'automations', label: 'Otomasyonlar', icon: GitBranch },
      { path: 'decision-matrix', label: 'Lojistik Karar Matrisi', icon: Brain },
      { path: 'offer-requests', label: 'Teklif Taleplerim', icon: FileSignature },
      { path: 'warehouse-ramp-assignment', label: 'Rampa Atama', icon: GitBranch },
      { path: 'beklemeler', label: 'Beklemeler', icon: AlertTriangle },
      { path: 'arrival-tracking', label: 'Varış Takibi', icon: MapPin },
      { path: 'point-visit-logs', label: 'Boşaltma / Ara Teslim Logları', icon: MapPin },
      { path: 'base-prices', label: 'Baz Fiyatları', icon: DollarSign },
    ]
  },
  {
    path: '/vehicle-admin-menu',
    label: 'Araç Yönetimi',
    icon: Truck,
    children: [
       { path: 'vehicle-management', label: 'Çekici Yönetimi', icon: Truck },
       { path: 'vehicle-documents', label: 'Çekici/Dorse Belgeleri', icon: FileText },
       { path: 'weighbridge-points', label: 'Kantar Noktalari', icon: MapPin },
       { path: 'fuel-exception-points', label: 'Akaryakit Istisna Noktalari', icon: Fuel },
      { path: 'vehicle-inspection-forms', label: 'Araç Kontrol Formları', icon: ClipboardList },
      { path: 'vehicle-assignment-forms', label: 'Araç Zimmet Formları', icon: FileText },
      { path: 'vehicle-assignments', label: 'Geçici Görevlendirme', icon: CalendarCheck },
      { path: 'trailer-management', label: 'Dorse Yönetimi', icon: Truck },
      { path: 'driver-incident-reports', label: 'Kaza / Arıza Kayıtları', icon: AlertTriangle },
      { path: 'traffic-fines', label: 'Trafik Cezaları', icon: AlertTriangle },
      { path: 'seyir-driver-comparison', label: 'Seyir Karşılaştırma', icon: Activity },
      { path: 'shift-management', label: 'Vardiya Tanımları', icon: Clock },
      { path: 'shift-assignments', label: 'Vardiya Atamaları', icon: CalendarCheck },
    ]
  },
  { 
    path: '/personnel', 
    label: 'Personel Yönetimi', 
    icon: Users,
    children: [
      { path: 'drivers', label: 'Özmal Sürücülerimiz', icon: Truck },
      { path: 'driver-violations', label: 'İhlaller', icon: AlertTriangle },
      { path: 'personnel-files', label: 'Özlük Dosyaları', icon: FileText },
      { path: 'gate-entry-exit', label: 'Nizamiye Giriş / Çıkış', icon: Clock },
      { path: 'driver-leave-requests', label: 'Sürücü İzin Yönetimi', icon: CalendarClock },
      { path: 'driver-payrolls', label: 'Maaş Bordroları', icon: FileText },
    ]
  },
  {
    path: '/collaboration-management',
    label: 'İşbirliği Yönetimi',
    icon: Share2,
    children: [
      { path: 'driver-applications', label: 'Spot Sürücü Başvuruları', icon: ClipboardList },
      { path: 'approved-drivers', label: 'Spot Sürücüler', icon: UserCheck },
      { path: 'corporate-tracking-list', label: 'Kurumsal Nakliye Firmaları', icon: Building2 },
      { path: 'vehicle-management?ownerType=external', label: 'Kurumsal Dış Nakliye Çekicileri', icon: Truck },
      { path: 'bid-marketplace', label: 'Kurumsal Teklif Havuzu', icon: ShoppingCart },
      { path: 'my-bids', label: 'Kurumsal Verilen Teklifler', icon: TrendingDown },
      { path: 'active-jobs', label: 'Kurumsal Aktif Sevkiyatlar', icon: Briefcase },
    ]
  },
  {
    path: '/customer-warehouse-menu',
    label: 'Müşteri/Depo Yönetimi',
    icon: Warehouse,
    children: [
      { path: 'customer-management', label: 'Müşteriler', icon: Users },
      { path: 'warehouse-settings', label: 'Depo Tanımları', icon: Warehouse },
      { path: 'ramp-management', label: 'Rampa Tanımları', icon: Truck },
    ]
  },
  { 
    path: '/finance-menu', 
    label: 'Finans & Masraf', 
    icon: DollarSign,
    children: [
      { path: 'expense-submission', label: 'Masraf Girişi', icon: Receipt },
      { path: 'expense-approval', label: 'Masraf Onayı', icon: CheckSquare },
      { path: 'expense-categories', label: 'Masraf Kategorileri', icon: FolderOpen },
      { path: 'payment-orders', label: 'Ödeme Emirleri', icon: Banknote },
      { path: 'cost-analysis', label: 'Maliyet Analizi', icon: DollarSign },
    ]
  },
  {
    path: '/reports-menu',
    label: 'Raporlar',
    icon: FileText,
    children: [
      { path: 'reports', label: 'Dashboard Özeti', icon: LayoutGrid },
      { path: 'reports?tab=comparison', label: 'Dönem Karşılaştırma', icon: TrendingUpIcon },
      { path: 'reports?tab=predictive', label: 'Tahmin & Analitik', icon: Brain },
      { path: 'reports?tab=operation', label: 'Operasyon Özeti', icon: Activity },
      { path: 'reports?tab=financial', label: 'Finansal Rapor', icon: DollarSign },
      { path: 'reports?tab=driver', label: 'Sürücü Performans', icon: Users },
      { path: 'reports?tab=vehicle', label: 'Araç Performans', icon: Truck },
      { path: 'reports?tab=customer', label: 'Müşteri Analizi', icon: MapPin },
      { path: 'reports?tab=route', label: 'Güzergah Analizi', icon: PieChartIcon },
      { path: 'reports?tab=bid', label: 'Teklif Analizi', icon: Target },
      { path: 'reports?tab=ontime', label: 'Zamanında Teslimat', icon: Timer },
      { path: 'reports?tab=accounting', label: 'Muhasebe & Ödeme', icon: FileSpreadsheet },
      { path: 'reports?tab=waiting', label: 'Bekleme Süreleri', icon: Clock },
      { path: 'return-load-potential-report', label: 'Dönüş Yük Potansiyeli', icon: BarChart3 },
      { path: 'flexible-reports', label: 'Özel Rapor Oluşturucu', icon: BarChart3 },
      { path: 'bekleme-bedeli-raporu', label: 'Bekleme Bedeli Raporu', icon: Timer },
      { path: 'yatma-raporu', label: 'Yatma Raporu', icon: Timer },
      { path: 'spot-driver-location-logs', label: 'Spot Sürücü Konum Logları', icon: MapPin },
    ]
  },
  {
    path: '/notifications',
    label: 'Bildirimler',
    icon: Bell,
  },
  { 
    path: '/communication-menu', 
    label: 'İletişim', 
    icon: MessageCircle,
    children: [
      { path: 'support-center', label: 'Destek Merkezi', icon: LifeBuoy },
      { path: 'help-desk-guide', label: 'Kullanim Kilavuzu', icon: BookOpen },
      { path: 'bulk-sms', label: 'Toplu SMS', icon: MessageCircle },
      { path: 'whatsapp-send', label: 'WhatsApp API', icon: MessageCircle },
      { path: 'push-notifications', label: 'Push Bildirim Testi', icon: Bell },
    ]
  },
  { 
    path: '/system-definitions-menu', 
    label: 'Sistem Tanımları', 
    icon: PanelsTopLeft,
    children: [
      { path: 'supply-metadata', label: 'Form Alan Yönetimi', icon: PanelsTopLeft },
      { path: 'contract-management', label: 'Sözleşme Yönetimi', icon: FileText },
      { path: 'api-key-management', label: 'API Anahtarları', icon: Key },
      { path: 'share-targets', label: 'Paylaşım Hedefleri', icon: Share2 },
    ]
  },
  { 
    path: '/general-settings-menu', 
    label: 'Genel Ayarlar', 
    icon: SettingsIcon,
    children: [
      { path: 'profile', label: 'Profilim', icon: UserCircle },
      { path: 'user-management', label: 'Kullanıcı Yönetimi', icon: UserCog },
      { path: 'role-management', label: 'Rol Yönetimi', icon: Shield },
      { path: 'system-logs', label: 'Sistem Logları', icon: FileBarChart },
      { path: 'version-history', label: 'Versiyon Geçmişi', icon: GitBranch },
      { path: 'backup-management', label: 'Yedekleme Yönetimi', icon: HardDrive },
      { path: 'settings', label: 'Sistem Ayarları', icon: SettingsIcon },
      { path: 'whatsapp-web', label: 'WhatsApp Web', icon: MessageCircle },
    ]
  },
];

// Role-based navigation filtering
const getNavItemsForRole = (role: UserRole | undefined): NavItem[] => {
  if (!role) return navItems;

  switch (role) {
    case 'admin':
      return navItems; // Admin tüm sayfalara erişebilir
    
    case 'dispatcher':
    case 'planner':
      return navItems.filter(item => 
        ['/', '/tracking-list', '/reports-menu'].includes(item.path) ||
        item.children
      );

    case 'corporate-external-transport':
      return [
        { path: '/', label: 'Panelim', icon: Home },
        { path: '/bid-marketplace', label: 'Teklif Talepleri', icon: ShoppingCart },
        { path: '/my-bids', label: 'Verilen Teklifler', icon: TrendingDown },
        { path: '/active-jobs', label: 'Sevkiyatlar', icon: Briefcase },
        { path: '/corporate-drivers', label: 'Sürücülerim', icon: Users },
        { path: '/vehicle-management?ownerType=external', label: 'Çekicilerim', icon: Truck },
        { path: '/driver-mobile-earnings', label: 'Faturalar', icon: FileCheck },
        { path: '/support-center', label: 'Destek', icon: LifeBuoy },
        { path: '/corporate-profile', label: 'Profil', icon: UserCircle },
      ];

    case 'corporate-external-transport-user':
      return [
        { path: '/driver-mobile-home', label: 'Mobil Panel', icon: Home },
        { path: '/driver-mobile-dashboard', label: 'Sevkiyatlarım', icon: Briefcase },
        { path: '/driver-mobile-documents', label: 'Belgelerim', icon: FolderOpen },
        { path: '/driver-mobile-earnings', label: 'Muhasebe', icon: FileCheck },
        { path: '/driver-mobile-profile', label: 'Profil', icon: UserCircle },
      ];

    case 'corporate-external-transport-driver':
      return [
        { path: '/driver-mobile-home', label: 'Mobil Panel', icon: Home },
        { path: '/driver-mobile-assignments', label: 'Siparişlerim', icon: Briefcase },
        { path: '/driver-mobile-documents', label: 'Belgelerim', icon: FolderOpen },
        { path: '/active-jobs', label: 'Aktif İşlerim', icon: Briefcase },
        { path: '/driver-mobile-profile', label: 'Profil', icon: UserCircle },
      ];
    
    case 'internal-driver':
      return [
        { path: '/', label: 'Ana Sayfa', icon: Home },
        { path: '/driver-tracking', label: 'Sevkiyatlarım', icon: Navigation },
        { path: '/expense-submission', label: 'Masraf Girişi', icon: Receipt },
        { path: '/documents', label: 'Belgelerim', icon: FolderOpen },
        { path: '/settings', label: 'Ayarlar', icon: SettingsIcon },
      ];
    
    case 'external-driver':
      return [
        { path: '/', label: 'Ana Sayfa', icon: Home },
        { path: '/bid-marketplace', label: 'Teklif Pazarı', icon: ShoppingCart },
        { path: '/my-bids', label: 'Tekliflerim', icon: TrendingDown },
        { path: '/active-jobs', label: 'Aktif İşlerim', icon: Briefcase },
        { path: '/driver-tracking', label: 'Sevkiyat Takip', icon: Navigation },
        { path: '/documents', label: 'Belgelerim', icon: FolderOpen },
        { path: '/settings', label: 'Ayarlar', icon: SettingsIcon },
      ];
    
    case 'warehouse':
      return [
        { path: '/', label: 'Ana Sayfa', icon: Home },
        { path: '/warehouse-ramp-assignment', label: 'Rampa Atama', icon: GitBranch },
        { path: '/gate-entry-exit', label: 'Nizamiye Giriş / Çıkış', icon: Clock },
        { path: '/documents', label: 'Belgeler', icon: FolderOpen },
        { path: '/settings', label: 'Ayarlar', icon: SettingsIcon },
      ];
    
    default:
      return navItems;
  }
};

const getNavItemsForRoles = (roles: UserRole[] | undefined): NavItem[] => {
  if (!roles || roles.length === 0) return navItems;
  if (roles.includes('admin')) return navItems;
  const merged = new Map<string, NavItem>();
  roles.forEach((role) => {
    getNavItemsForRole(role).forEach((item) => {
      const existing = merged.get(item.path);
      if (!existing) {
        merged.set(item.path, item);
        return;
      }
      if (item.children?.length) {
        const childMap = new Map<string, NavItem>();
        (existing.children || []).forEach((child) => childMap.set(child.path, child));
        item.children.forEach((child) => childMap.set(child.path, child));
        merged.set(item.path, { ...existing, children: [...childMap.values()] });
      }
    });
  });
  return [...merged.values()];
};

// Mobile bottom navigation items
const getMobileBottomNavForRole = (role: UserRole | undefined): NavItem[] => {
  if (!role) return [];

  switch (role) {
    case 'admin':
    case 'dispatcher':
    case 'planner':
      return [
        { path: '/', label: 'Anasayfa', icon: Home },
        { path: '/live-tracking', label: 'Canlı Takip', icon: Activity },
        { path: '/tracking', label: 'Sipariş', icon: MapPin },
  { 
    path: '/reports-menu', 
    label: 'Raporlar & Analitik', 
    icon: FileText,
    children: [
      { path: 'reports', label: 'Dashboard Özeti', icon: LayoutGrid },
      { path: 'reports?tab=comparison', label: 'Dönem Karşılaştırma', icon: TrendingUpIcon },
      { path: 'reports?tab=predictive', label: 'Tahmin & Analitik', icon: Brain },
      { path: 'reports?tab=operation', label: 'Operasyon Özeti', icon: Activity },
      { path: 'reports?tab=financial', label: 'Finansal Rapor', icon: DollarSign },
      { path: 'reports?tab=driver', label: 'Sürücü Performans', icon: Users },
      { path: 'reports?tab=vehicle', label: 'Araç Performans', icon: Truck },
      { path: 'reports?tab=customer', label: 'Müşteri Analizi', icon: MapPin },
      { path: 'reports?tab=route', label: 'Güzergah Analizi', icon: PieChartIcon },
      { path: 'reports?tab=bid', label: 'Teklif Analizi', icon: Target },
      { path: 'reports?tab=ontime', label: 'Zamanında Teslimat', icon: Timer },
      { path: 'reports?tab=accounting', label: 'Muhasebe & Ödeme', icon: FileSpreadsheet },
      { path: 'reports?tab=waiting', label: 'Bekleme Süreleri', icon: Clock },
       { path: 'return-load-potential-report', label: 'Dönüş Yük Potansiyeli', icon: BarChart3 },
       { path: 'flexible-reports', label: 'Özel Rapor Oluşturucu', icon: BarChart3 },
       { path: 'bekleme-bedeli-raporu', label: 'Bekleme Bedeli Raporu', icon: Timer },
      { path: 'yatma-raporu', label: 'Yatma Raporu', icon: Timer },
      { path: 'spot-driver-location-logs', label: 'Spot Sürücü Konum Logları', icon: MapPin },
    ]
  },
        { path: '/settings', label: 'Ayarlar', icon: SettingsIcon },
      ];

    case 'corporate-external-transport':
      return [
        { path: '/', label: 'Panel', icon: Home },
        { path: '/active-jobs', label: 'İşler', icon: Briefcase },
        { path: '/corporate-drivers', label: 'Sürücü', icon: Users },
        { path: '/driver-mobile-earnings', label: 'Fatura', icon: FileCheck },
        { path: '/corporate-profile', label: 'Profil', icon: UserCircle },
      ];

    case 'corporate-external-transport-user':
      return [
        { path: '/driver-mobile-home', label: 'Anasayfa', icon: Home },
        { path: '/driver-mobile-dashboard', label: 'Sevkiyat', icon: Briefcase },
        { path: '/driver-mobile-earnings', label: 'Muhasebe', icon: FileCheck },
        { path: '/driver-mobile-documents', label: 'Belge', icon: FolderOpen },
        { path: '/driver-mobile-profile', label: 'Profil', icon: UserCircle },
      ];

    case 'corporate-external-transport-driver':
      return [
        { path: '/driver-mobile-home', label: 'Anasayfa', icon: Home },
        { path: '/driver-mobile-assignments', label: 'İşler', icon: Briefcase },
        { path: '/driver-mobile-documents', label: 'Belgeler', icon: FolderOpen },
        { path: '/driver-mobile-earnings', label: 'Kazanç', icon: FileCheck },
        { path: '/driver-mobile-profile', label: 'Profil', icon: UserCircle },
      ];
    
    case 'internal-driver':
    case 'external-driver':
      return [
        { path: '/', label: 'Anasayfa', icon: Home },
        { path: '/driver-tracking', label: 'Sevkiyatlar', icon: MapPin },
        { path: '/documents', label: 'Belgeler', icon: FolderOpen },
        { path: '/settings', label: 'Ayarlar', icon: SettingsIcon },
      ];
    
    case 'warehouse':
      return [
        { path: '/', label: 'Anasayfa', icon: Home },
        { path: '/warehouse-transfer', label: 'Transfer', icon: Warehouse },
        { path: '/documents', label: 'Belgeler', icon: FolderOpen },
        { path: '/settings', label: 'Ayarlar', icon: SettingsIcon },
      ];
    
    default:
      return [];
  }
};

const getMobileBottomNavForRoles = (roles: UserRole[] | undefined): NavItem[] => {
  if (!roles || roles.length === 0) return [];
  if (roles.includes('admin')) return getMobileBottomNavForRole('admin');
  const merged = new Map<string, NavItem>();
  roles.forEach((role) => {
    getMobileBottomNavForRole(role).forEach((item) => merged.set(item.path, item));
  });
  return [...merged.values()];
};

function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isImpersonating, restoreOriginalUser } = useAuth();
  const { canAccessPage, canAccessMenuPage } = usePermissions();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notificationPopups, setNotificationPopups] = useState<Array<{ key: string; notification: any }>>([]);
  const [seyirViolations, setSeyirViolations] = useState<any[]>([]);
  const [showSeyirViolationModal, setShowSeyirViolationModal] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const notificationCachePrimedRef = useRef(false);
  const storageUser = getUser();
  const [themeSaving, setThemeSaving] = useState(false);

  const handleThemeToggle = useCallback(async () => {
    const themeUserId = user?.id || storageUser?.id;
    const currentTheme = getStoredThemePreference(themeUserId);
    const nextTheme: ThemePreference = currentTheme === 'light' ? 'dark' : 'light';
    setStoredThemePreference(nextTheme, themeUserId);
    if (!themeUserId) return;
    try {
      setThemeSaving(true);
      await settingsService.saveMySetting(getUserThemePreferenceSettingKey(themeUserId), nextTheme, {
        category: 'user',
        description: 'Kullanıcı tema tercihi',
        type: 'string',
      });
    } catch (error) {
      console.error('Theme toggle save failed:', error);
    } finally {
      setThemeSaving(false);
    }
  }, [storageUser?.id, user?.id]);
  const effectiveRoles = Array.from(new Set([user?.role, ...(user?.roles || [])].filter(Boolean))) as UserRole[];
  const isCorporateSubUser = effectiveRoles.includes('corporate-external-transport-user') || (effectiveRoles.includes('corporate-external-transport') && Boolean((user as any)?.externalTransportAccountUserId || (user as any)?.external_transport_account_user_id));
  const isDriverRole = isCorporateSubUser || effectiveRoles.includes('internal-driver') || effectiveRoles.includes('external-driver');
  const isCorporateCarrierRole = effectiveRoles.includes('corporate-external-transport') && !isCorporateSubUser;
  const canSeeSeyirViolations = !isDriverRole && canAccessPage('/seyir-driver-comparison');
  const canGoBack = location.pathname !== '/' && location.pathname !== '';

  const filterNavItemsByPermission = useCallback((items: NavItem[]): NavItem[] => (
    items.flatMap((item) => {
      if (item.children?.length) {
        const children = item.children.filter((child) => canAccessMenuPage(`/${String(child.path)}`));
        return children.length > 0 ? [{ ...item, children }] : [];
      }

      return canAccessMenuPage(item.path) ? [item] : [];
    })
  ), [canAccessMenuPage]);

  const permittedNavItems = useMemo(
    () => filterNavItemsByPermission(getNavItemsForRoles(effectiveRoles)),
    [effectiveRoles, filterNavItemsByPermission]
  );

  const permittedMobileNavItems = useMemo(
    () => getMobileBottomNavForRoles(effectiveRoles).filter((item) => (
        item.children?.length
          ? item.children.some((child) => canAccessMenuPage(`/${String(child.path)}`))
          : canAccessMenuPage(item.path)
      )),
    [canAccessMenuPage, effectiveRoles]
  );

  const firstAccessiblePath = useMemo(() => {
    for (const item of permittedNavItems) {
      if (item.children?.[0]) return `/${item.children[0].path}`;
      return item.path;
    }
    return null;
  }, [permittedNavItems]);

  const getNotificationTarget = (notification: any) => {
    const entityType = notification.related_entity_type;
    const entityId = notification.related_entity_id;
    const type = String(notification.type || '').trim();

    if (entityType === 'bid_request') {
      if (effectiveRoles.includes('external-driver')) {
        if (type === 'bid') return entityId ? `/driver-mobile-offer/${entityId}` : '/my-bids';
        return entityId ? `/driver-mobile-offer/${entityId}` : '/driver-mobile-home';
      }
      return '/offer-requests';
    }
    if (effectiveRoles.includes('external-driver') && entityType === 'shipment' && type === 'bid') {
      return '/my-bids';
    }
    if (entityType === 'shipment') return entityId ? `/tracking?shipment=${entityId}` : '/offer-requests';
    if (entityType === 'driver_application' && entityId) return '/driver-applications';
    if (entityType === 'support_thread') return '/support';
    if (entityType === 'driver_incident_report') return '/driver-incident-reports';
    if (entityType === 'waiting-record') return '/beklemeler';
    return null;
  };

  const handleNotificationNavigate = async (notification: any) => {
    const target = getNotificationTarget(notification);
    if (!target) return;
    try {
      if (!(notification.read ?? notification.is_read)) {
        await notificationService.markAsRead(notification.id);
        setNotifications((current) => current.map((item) => item.id === notification.id ? { ...item, is_read: true, read: true } : item));
      }
    } catch (error) {
      console.error('Failed to mark notification as read before navigation:', error);
    }
    setShowNotifications(false);
    navigate(target);
  };

  const dismissNotificationPopup = useCallback((key: string) => {
    setNotificationPopups((current) => current.filter((item) => item.key !== key));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);

  useEffect(() => {
    initializeSampleData();
  }, []);

  const loadNotifications = useCallback(async () => {
    if (!user?.id) {
      setNotifications([]);
      setSupportUnreadCount(0);
      return;
    }

    try {
      const [notificationResponse, supportResponse] = await Promise.all([
        notificationService.getNotifications({ userId: String(user.id), compact: true, limit: 50 }),
        supportService.getThreads().catch(() => ({ data: [] })),
      ]);
      const items = Array.isArray(notificationResponse?.data) ? notificationResponse.data : [];
      const supportThreads = Array.isArray((supportResponse as any)?.data)
        ? (supportResponse as any).data
        : Array.isArray(supportResponse)
          ? supportResponse
          : [];
      if (!notificationCachePrimedRef.current) {
        primeNotificationAlertCache('admin-panel', user.id, items);
        notificationCachePrimedRef.current = true;
      } else {
        announceNewNotifications('admin-panel', user.id, items);
      }
      setNotifications(items);
      setSupportUnreadCount(supportThreads.reduce((sum: number, thread: any) => sum + Number(thread.unreadCount || 0), 0));
    } catch (error) {
      console.error('Failed to load notifications in layout:', error);
    }
  }, [user?.id]);

  useEffect(() => {
    void loadNotifications();
  }, [location, loadNotifications]);

  useEffect(() => {
    notificationCachePrimedRef.current = false;
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const intervalId = window.setInterval(() => {
      void loadNotifications();
    }, 10000);
    return () => window.clearInterval(intervalId);
  }, [user?.id, loadNotifications]);

  useEffect(() => {
    if (!user?.id || !canSeeSeyirViolations) return;
    const modalKey = `seyir-violations-modal-${user.id}-${new Date().toISOString().slice(0, 10)}`;
    if (sessionStorage.getItem(modalKey) === 'shown') return;
    seyirViolationService.getViolations({ todayOnly: true, limit: 50 })
      .then((response: any) => {
        const rows = Array.isArray(response?.data) ? response.data : [];
        if (rows.length === 0) return;
        setSeyirViolations(rows);
        setShowSeyirViolationModal(true);
        sessionStorage.setItem(modalKey, 'shown');
      })
      .catch((error) => console.error('Seyir violation modal load failed:', error));
  }, [canSeeSeyirViolations, user?.id]);

  useEffect(() => {
    if (notificationPopups.length === 0) return;
    const timers = notificationPopups.map((item) => window.setTimeout(() => dismissNotificationPopup(item.key), 5000));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [notificationPopups, dismissNotificationPopup]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    const mainContent = document.querySelector('main');
    if (mainContent) {
      mainContent.scrollTop = 0;
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isDriverRole) return;
    const currentRoute = `${location.pathname}${location.search}`;
    if (canAccessPage(currentRoute)) return;
    if (!firstAccessiblePath || firstAccessiblePath === location.pathname) return;
    navigate(firstAccessiblePath, { replace: true });
  }, [canAccessPage, firstAccessiblePath, isDriverRole, location.pathname, location.search, navigate]);

  useEffect(() => {
    const route = `${location.pathname}${location.search}`;
    const currentParent = navItems.find((item) => item.children?.some((child) => `/${child.path}` === route));
    setExpandedMenus((current) => ({
      '/tracking-list': currentParent?.path === '/tracking-list',
      '/vehicle-admin-menu': currentParent?.path === '/vehicle-admin-menu',
      '/personnel': currentParent?.path === '/personnel',
      '/customer-warehouse-menu': currentParent?.path === '/customer-warehouse-menu',
      '/finance-menu': currentParent?.path === '/finance-menu',
      '/communication-menu': currentParent?.path === '/communication-menu',
      '/collaboration-management': currentParent?.path === '/collaboration-management',
      '/system-definitions-menu': currentParent?.path === '/system-definitions-menu',
      '/general-settings-menu': currentParent?.path === '/general-settings-menu',
      ...(currentParent ? { [currentParent.path]: true } : {}),
    }));
  }, [location.pathname, location.search]);

  const unreadCount = notifications.filter((n) => !(n.read ?? n.is_read)).length;
  const visibleNotifications = notifications.slice(0, 50);

  const getNavBadgeCount = (item: NavItem) => {
    if (!canAccessPage('/support-center')) return 0;
    if (item.path === '/communication-menu') return supportUnreadCount;
    if (item.path === 'support-center') return supportUnreadCount;
    return 0;
  };

  const handleMarkAllRead = () => {
    if (!user?.id) return;
    notificationService.markAllAsRead(String(user.id))
      .then(() => setNotifications((current) => current.map((item) => ({ ...item, read: true, is_read: true }))))
      .catch((error) => console.error('Failed to mark all notifications as read:', error));
  };

  const toggleMenu = (path: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  const currentRoute = `${location.pathname}${location.search}`;
  const resolveItemPath = (path: string) => path.startsWith('/') ? path : `/${path}`;

  const isSameRoute = (path: string) => {
    const target = resolveItemPath(path);
    return target.includes('?') ? currentRoute === target : location.pathname === target;
  };

  const isPathActive = (itemPath: string, children?: NavItem[]) => {
    if (children) {
      return children.some(child => isSameRoute(child.path));
    }
    return isSameRoute(itemPath);
  };

  const renderNavItem = (item: NavItem, isChild = false) => {
    const Icon = item.icon;
    const isActive = isPathActive(item.path, item.children);
    const isExpanded = expandedMenus[item.path];
    const hasChildren = item.children && item.children.length > 0;

    if (sidebarCollapsed && !isChild) {
      const linkPath = item.children && item.children.length > 0 ? resolveItemPath(item.children[0].path) : resolveItemPath(item.path);
      const badgeCount = getNavBadgeCount(item);
      return (
        <Link
          key={item.path}
          to={linkPath}
          onClick={(event) => { event.preventDefault(); window.location.href = linkPath; }}
          title={item.label}
          className={`relative flex h-14 items-center justify-center rounded-lg border transition-all text-sm ${
            currentRoute === linkPath || (item.children?.some(child => currentRoute === resolveItemPath(child.path)))
              ? 'border-orange-300 bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/25'
              : 'border-orange-500/15 bg-[#111111] text-slate-300 hover:border-orange-400/30 hover:bg-[#1a1a1a]'
          }`}
        >
          <Icon className="w-5 h-5 flex-shrink-0" />
          {badgeCount > 0 && (
            <span className="absolute -right-1 -top-1 inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
              {badgeCount}
            </span>
          )}
        </Link>
      );
    }

    if (hasChildren) {
      return (
        <div key={item.path} className="rounded-xl border border-orange-500/15 bg-[#111111] p-0 shadow-sm overflow-hidden">
          <button
            onClick={() => {
              if (isExpanded) {
                setExpandedMenus({});
                return;
              }

              setExpandedMenus({ [item.path]: true });
              if (item.children?.[0]) navigate(`/${item.children[0].path}`);
            }}
            className={`w-full flex items-center justify-between gap-3 rounded-lg px-[14px] py-2.5 transition-colors text-sm ${
              isActive
                ? 'bg-orange-500/15 text-white font-medium ring-1 ring-orange-400/30'
                : 'text-slate-200 hover:bg-white/5'
            }`}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${isActive ? 'bg-orange-500/20 text-orange-300 ring-1 ring-orange-400/30' : 'bg-white/5 text-slate-300'}`}>
                <Icon className="w-5 h-5 flex-shrink-0" />
              </div>
              <div className="min-w-0 text-left">
                <div className="whitespace-normal leading-tight text-[14px] font-medium">{item.label}</div>
                <div className="mt-1 text-[11px] text-slate-500">{item.children?.length || 0} alt menü</div>
              </div>
            </div>
             {isExpanded ? <ChevronDown className="w-4 h-4 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 flex-shrink-0" />}
           </button>
          {isExpanded && (
            <div className="mt-3 grid grid-cols-2 gap-2.5 px-3 pb-3">
              {item.children.map(child => renderNavItem(child, true))}
            </div>
          )}
        </div>
      );
    }

    const linkPath = resolveItemPath(item.path);
    const badgeCount = getNavBadgeCount(item);

    return (
      <Link
        key={item.path}
        to={linkPath}
        onClick={(event) => { event.preventDefault(); window.location.href = linkPath; }}
        className={`${isChild ? 'relative flex min-h-[80px] flex-col items-center justify-center rounded-lg border px-3 py-2 text-center transition-all' : 'relative flex items-center gap-3 rounded-xl border px-4 py-3 transition-all'} ${
          currentRoute === linkPath
            ? isChild
              ? 'border-orange-300 bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/25'
              : 'border-orange-300 bg-orange-500/15 text-orange-100 ring-1 ring-orange-400/30'
            : isChild
              ? 'border-orange-500/15 bg-[#111111] text-slate-200 hover:border-orange-400/30 hover:bg-[#1a1a1a]'
              : 'border-orange-500/15 bg-[#111111] text-slate-200 hover:border-orange-400/30 hover:bg-[#1a1a1a]'
        }`}
      >
        {isChild ? (
          <>
            <Icon className="mb-2 h-5 w-5 flex-shrink-0" />
            <span className="whitespace-normal leading-tight text-[13px] font-medium">{item.label}</span>
            {badgeCount > 0 && (
              <span className="absolute right-2 top-2 inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                {badgeCount}
              </span>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${currentRoute === linkPath ? 'bg-white text-orange-600' : 'bg-white/5 text-slate-300'}`}>
                <Icon className="w-5 h-5 flex-shrink-0" />
              </div>
              <span className="whitespace-normal leading-tight text-[15px] font-medium">{item.label}</span>
            </div>
            {badgeCount > 0 && (
              <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                {badgeCount}
              </span>
            )}
            <span className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
          </>
        )}
      </Link>
    );
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    const handleSessionExpired = () => {
      const message = (() => {
        try {
          return sessionStorage.getItem(SESSION_EXPIRED_MESSAGE_KEY) || 'Oturum süresi doldu, tekrar giriş yapın';
        } catch {
          return 'Oturum süresi doldu, tekrar giriş yapın';
        }
      })();
      toast.error(message);
      logout();
      navigate('/login', { replace: true });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [logout, navigate]);

  const handleRestoreOriginalUser = useCallback(async () => {
    const response = await restoreOriginalUser();
    if (!response.success) return;
    if (isAnyDriverUser(response.user)) {
      navigate('/driver-mobile-home');
      return;
    }
    navigate('/');
  }, [navigate, restoreOriginalUser]);

  return (
    <div className={`${isCorporateCarrierRole ? 'min-h-screen bg-slate-50 text-slate-900' : 'min-h-screen bg-[#050505] text-slate-100'} pb-16 lg:h-dvh lg:overflow-hidden lg:pb-0`}>
      {/* Header */}
      <header
        className={`${isCorporateCarrierRole ? 'hidden border-b border-slate-200 bg-white/95 text-slate-900 lg:block' : 'border-b border-orange-500/15 bg-[#111111]/95 text-slate-100'} sticky top-0 z-50 shadow-sm backdrop-blur`}
        style={{ paddingTop: 'var(--safe-area-top)' }}
      >
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex min-h-16 items-center justify-between gap-3 py-1">
            <div className="flex items-center gap-3">
              {canGoBack && (
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className={`${isCorporateCarrierRole ? 'text-slate-700 hover:bg-slate-100' : 'text-slate-300 hover:bg-white/5'} inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg lg:hidden`}
                  aria-label="Geri git"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
              )}
              <img src={dogusLogo} alt="Doğuş Logo" className="w-10 h-10 object-contain" />
              <div className="min-w-0">
                <h1 className={`${isCorporateCarrierRole ? 'text-slate-950' : 'text-white'} truncate text-lg font-bold tracking-tight sm:text-xl`}>Yük Ortağım</h1>
                <p className={`${isCorporateCarrierRole ? 'text-slate-500' : 'text-slate-400'} hidden text-xs sm:block`}>{isCorporateCarrierRole ? 'Kurumsal Nakliye Paneli' : 'Doğuş Çay Lojistik Operasyon Platformu'}</p>
              </div>
            </div>
            
            {/* Desktop actions */}
            <div className="hidden lg:flex items-center gap-4">
              {/* Notifications */}
              <div className="relative" ref={notificationRef}>
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 rounded-lg text-slate-300 hover:bg-white/5"
                >
                  <Bell className="w-6 h-6" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>
                
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-96 bg-[#111111] rounded-lg shadow-xl border border-orange-500/15 max-h-[500px] overflow-y-auto">
                    <div className="p-4 border-b border-orange-500/15 flex items-center justify-between">
                      <h3 className="font-semibold text-white">Bildirimler</h3>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => { setShowNotifications(false); navigate('/notifications'); }}
                          className="text-sm font-medium text-orange-300 hover:text-orange-200"
                        >
                          Tümünü Gör ({notifications.length})
                        </button>
                        {unreadCount > 0 && (
                          <button
                            onClick={handleMarkAllRead}
                            className="text-sm text-orange-300 hover:text-orange-200"
                          >
                            Tümünü Okundu İşaretle
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="divide-y divide-gray-200">
                      {visibleNotifications.length === 0 ? (
                        <p className="p-4 text-sm text-slate-400 text-center">Bildirim yok</p>
                      ) : (
                        visibleNotifications.map(notif => (
                          <div key={notif.id} className={`p-4 hover:bg-white/5 ${!(notif.read ?? notif.is_read) ? 'bg-orange-500/10' : ''}`}>
                            <div className="flex items-start gap-3">
                              <div className={`mt-1 w-2 h-2 rounded-full ${
                                notif.type === 'error' ? 'bg-red-500' :
                                notif.type === 'warning' ? 'bg-orange-500' :
                                notif.type === 'success' ? 'bg-green-500' : 'bg-blue-500'
                              }`} />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-white">{notif.title}</p>
                                <p className="text-sm text-slate-300 mt-1">{notif.message}</p>
                                <p className="text-xs text-slate-500 mt-1">
                                  {new Date(notif.timestamp || notif.created_at || notif.updated_at || Date.now()).toLocaleString('tr-TR')}
                                </p>
                                {getNotificationTarget(notif) && (
                                  <button
                                    onClick={() => handleNotificationNavigate(notif)}
                                    className="mt-2 text-xs font-medium text-orange-300 hover:text-orange-200"
                                  >
                                    Bildirime Git
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    {notifications.length > visibleNotifications.length ? <div className="border-t border-orange-500/15 p-3 text-center text-xs text-slate-400">Kutucukta son 50 bildirim gösteriliyor</div> : null}
                  </div>
                )}
              </div>
              
              {/* User info & Logout */}
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-sm font-medium text-white">{user?.name || storageUser?.name || 'Kullanıcı'}</p>
                  <p className="text-xs text-slate-400">
                    {user?.role === 'admin' ? 'Yönetici' :
                     user?.role === 'dispatcher' ? 'Dispozisyon Uzmanı' :
                     user?.role === 'internal-driver' ? 'Sürücü (İç)' :
                     user?.role === 'external-driver' ? 'Sürücü (Dış)' :
                      user?.role === 'warehouse' ? 'Depo Görevlisi' :
                      isCorporateCarrierRole ? 'Kurumsal Nakliye' : 'Operasyon'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void handleThemeToggle()}
                  disabled={themeSaving}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-orange-500/20 bg-white/5 text-amber-300 hover:bg-white/10 disabled:opacity-50"
                  title="Temayı değiştir"
                >
                  <Lightbulb className="h-5 w-5" />
                </button>
                <div className="w-10 h-10 rounded-full bg-orange-600 flex items-center justify-center text-white font-semibold">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-lg text-slate-300 hover:bg-white/5"
                  title="Çıkış Yap"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Mobile - User info & notifications */}
            <div className="flex lg:hidden items-center gap-1 self-start pt-1">
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 rounded-lg text-slate-300 hover:bg-white/5"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>
              </div>
              
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="mt-0.5 rounded-md p-2 text-slate-300 hover:bg-white/5"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {notificationPopups.length > 0 && (
        <div className="fixed right-4 top-20 z-[90] w-[min(420px,calc(100vw-2rem))]">
          <div className="relative">
            {notificationPopups.map(({ key, notification }, index) => (
              <div
                key={key}
                className={`rounded-2xl border border-amber-200 bg-white p-4 shadow-2xl dark:border-amber-400/30 dark:bg-[#111111] ${index === 0 ? 'relative' : 'absolute left-0 right-0'}`}
                style={index === 0 ? { zIndex: 10 } : { top: 0, zIndex: notificationPopups.length - index, transform: `scale(${1 - index * 0.03})`, opacity: 1 - index * 0.2, pointerEvents: 'none' }}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-amber-400" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">{notification.title || 'Yeni bildirim'}</p>
                      <button type="button" onClick={() => dismissNotificationPopup(key)} className="text-slate-400 hover:text-slate-900 dark:hover:text-white">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">{notification.message || 'Yeni bir bildiriminiz var.'}</p>
                    <p className="mt-2 text-xs text-slate-500">{new Date(notification.created_at || notification.updated_at || Date.now()).toLocaleString('tr-TR')}</p>
                    {getNotificationTarget(notification) && (
                      <button
                        type="button"
                        onClick={() => {
                          dismissNotificationPopup(key);
                          void handleNotificationNavigate(notification);
                        }}
                        className="mt-3 rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100 dark:bg-amber-500/15 dark:text-amber-300 dark:hover:bg-amber-500/25"
                      >
                        Bildirime Git
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={`flex ${isCorporateCarrierRole ? 'min-h-screen lg:h-[calc(100dvh-4.5rem)]' : 'lg:h-[calc(100dvh-4.5rem)]'} lg:overflow-hidden`}>
        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <>
            <button
              type="button"
              aria-label="Mobil menuyu kapat"
              onClick={() => setIsMobileMenuOpen(false)}
              className="lg:hidden fixed inset-0 bg-black/40 z-40"
            />
              <aside
                className="lg:hidden fixed right-0 bottom-16 w-[88%] max-w-sm bg-[#111111] border-l border-orange-500/15 z-50 overflow-y-auto shadow-2xl"
                style={{ top: 'calc(4.5rem + var(--safe-area-top))' }}
              >
              <div className="p-4 border-b border-orange-500/15">
                <p className="text-sm font-semibold text-white">Menu</p>
                <p className="text-xs text-slate-400 mt-1">
                  {user?.name || storageUser?.name || 'Kullanici'}
                </p>
              </div>
              <nav className="p-4 space-y-1">
                 {permittedNavItems.map((item) => {
                  const hasChildren = item.children && item.children.length > 0;

                  if (hasChildren) {
                    return (
                      <div key={item.path}>
                        <button
                          onClick={() => {
                            if (expandedMenus[item.path]) {
                              setExpandedMenus({});
                              return;
                            }

                            setExpandedMenus({ [item.path]: true });
                            if (item.children?.[0]) {
                              navigate(`/${item.children[0].path}`);
                            }
                          }}
                          className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg transition-colors text-sm ${
                            isPathActive(item.path, item.children)
                              ? 'bg-orange-500/15 text-white font-medium'
                              : 'text-slate-200 hover:bg-white/5'
                          }`}
                        >
                          <div className="flex items-center gap-3 min-w-0 flex-1 text-left">
                            <item.icon className="w-5 h-5 flex-shrink-0" />
                            <span className="whitespace-normal leading-tight">{item.label}</span>
                          </div>
                          {expandedMenus[item.path] ? (
                            <ChevronDown className="w-4 h-4 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-4 h-4 flex-shrink-0" />
                          )}
                        </button>
                        {expandedMenus[item.path] && (
                          <div className="ml-4 mt-1 space-y-1">
                            {item.children?.map((child) => {
                              const ChildIcon = child.icon;
                              return (
                                <Link
                                  key={child.path}
                                  to={resolveItemPath(child.path)}
                                  onClick={() => setIsMobileMenuOpen(false)}
                                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-sm pl-8 ${
                                    currentRoute === resolveItemPath(child.path)
                                      ? 'bg-orange-500 text-white font-medium'
                                      : 'text-slate-200 hover:bg-white/5'
                                  }`}
                                >
                                  <ChildIcon className="w-5 h-5 flex-shrink-0" />
                                  <span className="whitespace-normal leading-tight">{child.label}</span>
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }

                  const ItemIcon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-sm ${
                        currentRoute === resolveItemPath(item.path)
                          ? 'bg-orange-500 text-white font-medium'
                          : 'text-slate-200 hover:bg-white/5'
                      }`}
                    >
                      <ItemIcon className="w-5 h-5 flex-shrink-0" />
                      <span className="whitespace-normal leading-tight">{item.label}</span>
                    </Link>
                  );
                })}
              </nav>
              <div className="p-4 border-t border-orange-500/15">
                <button
                  onClick={handleLogout}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-orange-600 text-white py-3 text-sm font-medium"
                >
                  <LogOut className="w-4 h-4" />
                  Cikis Yap
                </button>
              </div>
            </aside>
          </>
        )}

        {/* Sidebar - Desktop */}
        <aside className={`${isDriverRole ? 'hidden' : 'hidden lg:flex lg:flex-col'} ${sidebarCollapsed ? 'w-24 min-w-24' : 'w-[310px] min-w-[310px]'} ${isCorporateCarrierRole ? 'border-r border-slate-200 bg-white' : 'border-r border-orange-500/15 bg-gradient-to-b from-[#111111] to-[#0a0a0a]'} shadow-sm transition-all duration-300 lg:h-full lg:overflow-y-auto`}>
          <nav className="flex-1 p-4 space-y-2.5">
            {/* Toggle Button */}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 rounded-2xl transition-colors text-sm bg-[#111111] border border-orange-500/15 hover:bg-[#1a1a1a] ${
                  sidebarCollapsed ? '' : 'mb-2'
                }`}
              title={sidebarCollapsed ? 'Menüyü Aç' : 'Menüyü Kapat'}
            >
              <div className="flex items-center gap-3">
                <Menu className="w-5 h-5 flex-shrink-0 text-orange-300" />
                {!sidebarCollapsed && <span className="text-[15px] font-medium text-slate-100">Menü</span>}
              </div>
              {!sidebarCollapsed && <ChevronLeft className="w-4 h-4 text-slate-400" />}
            </button>
            
            {/* Menu Items */}
             {permittedNavItems.map(item => renderNavItem(item))}
          </nav>

          {!sidebarCollapsed && (
            <div className="border-t border-orange-500/15 p-4 space-y-3">
              <div className="rounded-xl border border-orange-500/15 bg-[#111111] px-4 py-4 shadow-sm">
                <div className="text-sm font-semibold text-white">{user?.name || storageUser?.name || 'Kullanıcı'}</div>
                <div className="mt-1 text-xs text-slate-400">{user?.email || storageUser?.email || ''}</div>
              </div>
              <button onClick={() => navigate('/settings')} className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-slate-200 hover:bg-white/5" type="button">
                <Key className="h-5 w-5" />
                <span className="text-sm font-medium">Şifre Değiştir</span>
              </button>
              <button onClick={handleLogout} className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-orange-300 hover:bg-orange-500/10" type="button">
                <LogOut className="h-5 w-5" />
                <span className="text-sm font-medium">Çıkış Yap</span>
              </button>
            </div>
          )}

          {sidebarCollapsed && (
            <div className="border-t border-orange-500/15 p-3 space-y-2">
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-orange-600 flex items-center justify-center text-white font-semibold mb-2">
                  {(user?.name || storageUser?.name || 'U').charAt(0)}
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center justify-center w-10 h-10 rounded-lg text-orange-300 hover:bg-orange-500/10 transition-colors"
                  title="Çıkış Yap"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main className={`min-w-0 flex-1 overflow-x-hidden ${isCorporateCarrierRole ? 'bg-slate-50 px-0 pb-24 pt-0 sm:px-0 sm:pt-0' : 'bg-gradient-to-b from-[#0a0a0a] to-[#111111] px-3 pb-24 pt-4 sm:px-6 sm:pb-24 sm:pt-5'} transition-all duration-300 lg:h-full lg:overflow-y-auto lg:p-6 lg:pb-6 ${sidebarCollapsed ? 'ml-0' : ''}`}>
          <div className="w-full min-w-0 max-w-full">
            {isImpersonating && (
              <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-amber-300/40 bg-amber-500/10 px-4 py-3 text-amber-100 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-sm font-semibold">Taklit oturumu aktif</div>
                  <div className="text-xs text-amber-100/80">
                    {user?.impersonatedByName || 'Ana hesap'} hesabından {user?.name || user?.username || 'kullanıcı'} olarak giriş yapıldı.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRestoreOriginalUser}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-amber-300"
                >
                  <CornerUpLeft className="h-4 w-4" />
                  Ana Hesaba Geri Dön
                </button>
              </div>
            )}
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className={`${isDriverRole ? 'fixed' : 'fixed lg:hidden'} bottom-0 left-0 right-0 z-50 ${isCorporateCarrierRole ? 'border-t border-slate-200 bg-white/95 shadow-[0_-14px_34px_rgba(15,23,42,0.10)]' : 'border-t border-orange-500/15 bg-[#111111]/98'} backdrop-blur-xl`}>
          <div className="flex overflow-x-auto px-2 pb-[max(env(safe-area-inset-bottom),0.5rem)] pt-2">
           {permittedMobileNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = isPathActive(item.path);
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex min-w-[68px] flex-1 shrink-0 flex-col items-center justify-center rounded-2xl px-2 py-2 transition-colors ${
                  isActive
                    ? isCorporateCarrierRole ? 'bg-slate-950 text-white shadow-lg shadow-slate-300/60' : 'bg-orange-500/10 text-orange-300'
                    : isCorporateCarrierRole ? 'text-slate-500 hover:text-slate-900' : 'text-slate-400 hover:text-white'
                }`}
              >
                <Icon className={`w-6 h-6 ${isActive ? 'fill-orange-500/20' : ''}`} />
                <span className="mt-1 max-w-full text-center text-[10px] font-medium leading-tight sm:text-[11px]">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {showSeyirViolationModal && seyirViolations.length > 0 ? (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-5xl overflow-hidden rounded-2xl border border-rose-500/30 bg-white shadow-2xl dark:bg-[#111111]">
            <div className="flex items-start justify-between gap-4 border-b border-rose-200 bg-rose-50 px-5 py-4 dark:border-rose-500/20 dark:bg-rose-950/30">
              <div>
                <div className="flex items-center gap-2 text-lg font-bold text-rose-800 dark:text-rose-100">
                  <AlertTriangle className="h-5 w-5" />
                  Bugünkü Seyir Dijital Ehliyet İhlalleri
                </div>
                <p className="mt-1 text-sm text-rose-700 dark:text-rose-200">Seyir'den gelen dijital ehliyet numarası sistemdeki aktif özmal sürücüler veya atama bilgisiyle uyuşmuyor.</p>
              </div>
              <button type="button" onClick={() => setShowSeyirViolationModal(false)} className="rounded-lg p-2 text-rose-700 hover:bg-rose-100 dark:text-rose-100 dark:hover:bg-white/10">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-auto p-4">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-white text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-[#111111] dark:text-slate-400">
                  <tr>
                    <th className="px-3 py-2">Tarih/Saat</th>
                    <th className="px-3 py-2">Tip</th>
                    <th className="px-3 py-2">Çekici</th>
                    <th className="px-3 py-2">Dorse</th>
                    <th className="px-3 py-2">Araç Sürücüsü</th>
                    <th className="px-3 py-2">Araç Ehliyet No</th>
                    <th className="px-3 py-2">Seyir Ehliyet No</th>
                    <th className="px-3 py-2">Seyir Sürücü</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {seyirViolations.slice(0, 20).map((row) => (
                    <tr key={row.id} className="text-slate-700 dark:text-slate-200">
                      <td className="px-3 py-2">{row.last_detected_at ? new Date(row.last_detected_at).toLocaleString('tr-TR') : '-'}</td>
                      <td className="px-3 py-2">{row.violation_type === 'unknown_digital_license' ? 'Bilinmeyen dijital ehliyet' : 'Atanan sürücü farklı'}</td>
                      <td className="px-3 py-2 font-semibold">{row.tractor_plate || '-'}</td>
                      <td className="px-3 py-2">{row.trailer_plate || '-'}</td>
                      <td className="px-3 py-2">{row.system_driver_name || '-'}</td>
                      <td className="px-3 py-2">{row.system_driver_license_no || '-'}</td>
                      <td className="px-3 py-2 font-semibold text-rose-700 dark:text-rose-200">{row.seyir_digital_license_no || '-'}</td>
                      <td className="px-3 py-2">{row.seyir_driver_name || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-col gap-2 border-t border-slate-200 px-5 py-4 sm:flex-row sm:justify-end dark:border-slate-800">
              <button type="button" onClick={() => setShowSeyirViolationModal(false)} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-white/5">Kapat</button>
              <button type="button" onClick={() => { setShowSeyirViolationModal(false); navigate('/seyir-driver-comparison'); }} className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700">Geçmiş Raporu Aç</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export { Layout };
export default Layout;
